import React, { useEffect, useRef, useState, useCallback } from 'react';
import { renderGenerator } from '../utils/genArtRenderers';
import { GENERATORS, FAMILIES, PALETTES, getDefaultPalette, getDefaultParams } from '../constants/genArt';

/**
 * Test page for generative art renderers.
 * Renders all 20 approved generators at reduced resolution for visual verification.
 * Route: /test-genart
 */

const THUMB_W = 270;
const THUMB_H = 337; // 4:5 aspect ratio

function GeneratorCard({ generator, seed }) {
  const canvasRef = useRef(null);
  const [rendering, setRendering] = useState(true);
  const [renderTime, setRenderTime] = useState(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    setRendering(true);
    const t0 = performance.now();

    // Use requestAnimationFrame to not block UI
    requestAnimationFrame(() => {
      const paletteId = getDefaultPalette(generator.family);
      const params = getDefaultParams(generator.id);

      renderGenerator(ctx, THUMB_W, THUMB_H, {
        type: generator.id,
        paletteId,
        seed,
        params,
      });

      const elapsed = performance.now() - t0;
      setRenderTime(elapsed);
      setRendering(false);
    });
  }, [generator, seed]);

  const family = FAMILIES.find(f => f.id === generator.family);

  return (
    <div style={{
      background: '#1a1a1e',
      borderRadius: 8,
      overflow: 'hidden',
      border: '1px solid #333',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <canvas
        ref={canvasRef}
        width={THUMB_W}
        height={THUMB_H}
        style={{ width: THUMB_W, height: THUMB_H, display: 'block' }}
      />
      <div style={{ padding: '8px 10px', fontSize: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#fff', fontWeight: 600 }}>{generator.name}</span>
          <span style={{
            color: family?.accent || '#888',
            fontSize: 10,
            padding: '1px 6px',
            border: `1px solid ${family?.accent || '#555'}`,
            borderRadius: 4,
          }}>
            {generator.family}
          </span>
        </div>
        <div style={{ color: '#888', fontSize: 10, marginTop: 2 }}>{generator.desc}</div>
        <div style={{
          color: rendering ? '#e8b84b' : '#34d399',
          fontSize: 10,
          marginTop: 4,
          fontFamily: 'monospace',
        }}>
          {rendering ? 'Rendering...' : `${renderTime.toFixed(0)}ms`}
        </div>
      </div>
    </div>
  );
}

function PaletteGrid() {
  return (
    <div style={{ marginTop: 40 }}>
      <h2 style={{ color: '#fff', fontSize: 18, marginBottom: 12 }}>
        All 41 Palettes
      </h2>
      {FAMILIES.map(fam => {
        const famPalettes = PALETTES.filter(p => p.family === fam.id);
        return (
          <div key={fam.id} style={{ marginBottom: 20 }}>
            <h3 style={{ color: fam.accent, fontSize: 14, marginBottom: 8 }}>
              {fam.name} ({famPalettes.length})
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {famPalettes.map(pal => (
                <div key={pal.id} style={{
                  background: '#1a1a1e',
                  borderRadius: 6,
                  padding: '6px 8px',
                  border: '1px solid #333',
                  minWidth: 140,
                }}>
                  <div style={{ fontSize: 11, color: '#ccc', marginBottom: 4 }}>{pal.name}</div>
                  <div style={{ display: 'flex', gap: 2 }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: 3,
                      background: pal.bg, border: '1px solid #444',
                    }} title="bg" />
                    {pal.colors.slice(0, 7).map((c, i) => (
                      <div key={i} style={{
                        width: 16, height: 16, borderRadius: 3,
                        background: c, border: '1px solid #444',
                      }} title={c} />
                    ))}
                    {pal.colors.length > 7 && (
                      <div style={{ width: 16, height: 16, borderRadius: 3, background: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: '#888' }}>
                        +{pal.colors.length - 7}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function TestGenArt() {
  const [seed, setSeed] = useState(42);
  const [filterFamily, setFilterFamily] = useState('all');
  const [renderKey, setRenderKey] = useState(0);

  const filteredGenerators = filterFamily === 'all'
    ? GENERATORS
    : GENERATORS.filter(g => g.family === filterFamily);

  const shuffleSeed = useCallback(() => {
    const newSeed = Math.floor(Math.random() * 99999);
    setSeed(newSeed);
    setRenderKey(k => k + 1);
  }, []);

  return (
    <div style={{
      background: '#0a0a0c',
      minHeight: '100vh',
      padding: '20px 30px',
      fontFamily: 'Inter, system-ui, sans-serif',
    }}>
      {/* Header */}
      <div style={{ marginBottom: 24, borderBottom: '1px solid #333', paddingBottom: 16 }}>
        <h1 style={{ color: '#fff', fontSize: 24, margin: 0 }}>
          Generative Art — Test Page
        </h1>
        <p style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
          Phase A visual verification. All 20 approved generators × default palettes.
        </p>
      </div>

      {/* Controls */}
      <div style={{
        display: 'flex', gap: 16, alignItems: 'center',
        marginBottom: 20, flexWrap: 'wrap',
      }}>
        {/* Family filter */}
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={() => setFilterFamily('all')}
            style={{
              padding: '4px 12px', borderRadius: 4, border: '1px solid #444',
              background: filterFamily === 'all' ? '#333' : 'transparent',
              color: filterFamily === 'all' ? '#fff' : '#888',
              cursor: 'pointer', fontSize: 12,
            }}
          >
            All ({GENERATORS.length})
          </button>
          {FAMILIES.map(fam => (
            <button
              key={fam.id}
              onClick={() => setFilterFamily(fam.id)}
              style={{
                padding: '4px 12px', borderRadius: 4,
                border: `1px solid ${filterFamily === fam.id ? fam.accent : '#444'}`,
                background: filterFamily === fam.id ? fam.accent + '22' : 'transparent',
                color: filterFamily === fam.id ? fam.accent : '#888',
                cursor: 'pointer', fontSize: 12,
              }}
            >
              {fam.name} ({fam.count})
            </button>
          ))}
        </div>

        {/* Seed control */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ color: '#888', fontSize: 12 }}>Seed:</span>
          <input
            type="number"
            value={seed}
            onChange={e => { setSeed(parseInt(e.target.value) || 0); setRenderKey(k => k + 1); }}
            style={{
              width: 80, padding: '4px 8px', borderRadius: 4,
              border: '1px solid #444', background: '#111', color: '#fff',
              fontSize: 12, fontFamily: 'monospace',
            }}
          />
          <button
            onClick={shuffleSeed}
            style={{
              padding: '4px 12px', borderRadius: 4, border: '1px solid #444',
              background: '#1a1a1e', color: '#fff', cursor: 'pointer', fontSize: 12,
            }}
          >
            🎲 Shuffle
          </button>
        </div>

        <div style={{ color: '#555', fontSize: 11 }}>
          Rendering at {THUMB_W}×{THUMB_H} (1/4 of 1080×1350)
        </div>
      </div>

      {/* Generator grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(${THUMB_W}px, 1fr))`,
        gap: 16,
      }}>
        {filteredGenerators.map(gen => (
          <GeneratorCard
            key={`${gen.id}-${renderKey}`}
            generator={gen}
            seed={seed}
          />
        ))}
      </div>

      {/* Palette grid */}
      <PaletteGrid />

      {/* Back link */}
      <div style={{ marginTop: 40, paddingTop: 16, borderTop: '1px solid #333' }}>
        <a href="/" style={{ color: '#30a0e0', fontSize: 13 }}>← Back to Builder</a>
      </div>
    </div>
  );
}
